import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';
import type { ModelOption, ModuleConfigRow, ProviderRow } from './types.js';

export const AI_ADMIN_QUERY_KEYS = {
  providers: ['ai-admin', 'providers'] as const,
  models: ['ai-admin', 'models'] as const,
  moduleConfigs: ['ai-admin', 'module-configs'] as const,
};

/**
 * Lista providers + modelo principal de cada um.
 *
 * Usa `.select(string)` com FK join. As colunas de chave (api_key_last4,
 * api_key_fingerprint, api_key_updated_at) foram adicionadas na migration
 * 0030 — tipamos o retorno via cast local porque `@kmgestao/db-types`
 * ainda não foi regenerado.
 */
interface RawProviderRow {
  id: string;
  key: string;
  display_name: string;
  base_url: string | null;
  enabled: boolean;
  api_key_last4: string | null;
  api_key_fingerprint: string | null;
  api_key_updated_at: string | null;
  ai_models: {
    id: string;
    model_id: string;
    display_name: string;
    sort_order: number;
    deprecated_at: string | null;
  }[];
}

export function useProviders() {
  return useQuery({
    queryKey: AI_ADMIN_QUERY_KEYS.providers,
    queryFn: async (): Promise<ProviderRow[]> => {
      const { data, error } = await supabase
        .from('ai_providers')
        .select(
          `
          id, key, display_name, base_url, enabled,
          api_key_last4, api_key_fingerprint, api_key_updated_at,
          ai_models ( id, model_id, display_name, sort_order, deprecated_at )
          `,
        )
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as RawProviderRow[];
      return rows.map((row) => {
        const primary = [...row.ai_models]
          .filter((m) => m.deprecated_at === null)
          .sort((a, b) => a.sort_order - b.sort_order)[0];

        return {
          id: row.id,
          key: row.key,
          displayName: row.display_name,
          baseUrl: row.base_url,
          enabled: row.enabled,
          apiKeyLast4: row.api_key_last4,
          apiKeyFingerprint: row.api_key_fingerprint,
          apiKeyUpdatedAt: row.api_key_updated_at,
          primaryModel: primary
            ? {
                id: primary.id,
                modelId: primary.model_id,
                displayName: primary.display_name,
              }
            : null,
        };
      });
    },
  });
}

/**
 * Lista TODOS os modelos — disponíveis e indisponíveis — usado pelos
 * dropdowns do "Atribuição por módulo" e pelo detector de "órfão"
 * (quando `ai_module_configs.default_model_id` aponta pra um modelo
 * desabilitado/deprecated ou de provider desabilitado).
 *
 * A flag `isAvailable` sinaliza se o modelo pode ser selecionado. Antes
 * dessa mudança a query filtrava indisponíveis aqui mesmo, e a UI não
 * tinha como distinguir "modelo foi deletado" de "modelo desabilitado" —
 * o `<select>` fazia fallback visual silencioso e enganava o Superadmin
 * (bug 2026-04-24). Join com provider para exibir "DeepSeek · deepseek-chat".
 */
interface RawModelRow {
  id: string;
  model_id: string;
  display_name: string;
  provider_id: string;
  deprecated_at: string | null;
  enabled: boolean;
  ai_providers: { id: string; display_name: string; enabled: boolean } | null;
}

export function useModels() {
  return useQuery({
    queryKey: AI_ADMIN_QUERY_KEYS.models,
    queryFn: async (): Promise<ModelOption[]> => {
      const { data, error } = await supabase
        .from('ai_models')
        .select(
          `
          id, model_id, display_name, provider_id, deprecated_at, enabled,
          ai_providers ( id, display_name, enabled )
          `,
        )
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as RawModelRow[];
      return rows.map((row) => ({
        id: row.id,
        modelId: row.model_id,
        providerId: row.provider_id,
        providerName: row.ai_providers?.display_name ?? '—',
        displayName: row.display_name,
        isAvailable:
          row.enabled === true && row.deprecated_at === null && row.ai_providers?.enabled === true,
      }));
    },
  });
}

/**
 * Lista configs de módulos de IA (tabela `ai_module_configs`).
 * Filtramos por `is_active = true`; apenas RLS vê esses dados para superadmin
 * (policy `ai_module_configs_select_active`).
 */
interface RawModuleConfigRow {
  id: string;
  module_key: string;
  name_pt_br: string;
  default_model_id: string | null;
  fallback_model_id: string | null;
  is_active: boolean;
}

export function useModuleConfigs() {
  return useQuery({
    queryKey: AI_ADMIN_QUERY_KEYS.moduleConfigs,
    queryFn: async (): Promise<ModuleConfigRow[]> => {
      const { data, error } = await supabase
        .from('ai_module_configs')
        .select('id, module_key, name_pt_br, default_model_id, fallback_model_id, is_active')
        .order('module_key', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as RawModuleConfigRow[];
      return rows.map((row) => ({
        id: row.id,
        moduleKey: row.module_key as ModuleConfigRow['moduleKey'],
        namePtBr: row.name_pt_br,
        defaultModelId: row.default_model_id,
        fallbackModelId: row.fallback_model_id,
        isActive: row.is_active,
      }));
    },
  });
}
